import React from "react";
import { ArrowUpRight, Mail } from "lucide-react";
import { SUBSTACK_URL, NEWSLETTER_NAME, NEWSLETTER_TAGLINE } from "../config/site";

/**
 * NewsletterCTA — orange-on-dark card prompting subscription to
 * "LinkedIn Lab Reports" on Substack.
 *
 * variant: "card"   (default — full card with copy + button)
 *          "compact" (single-row inline strip)
 *          "banner" (wider hero-style banner for top of list page)
 */
export default function NewsletterCTA({ variant = "card" }) {
  if (variant === "compact") {
    return (
      <a
        href={SUBSTACK_URL}
        target="_blank"
        rel="noreferrer"
        className="group inline-flex items-center gap-2 px-4 py-2 rounded-full text-[13px] font-medium transition"
        style={{
          background: "rgba(255,51,1,0.10)",
          color: "var(--agero-orange)",
        }}
      >
        <Mail size={14} />
        <span>Subscribe to {NEWSLETTER_NAME}</span>
        <ArrowUpRight
          size={14}
          className="transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
        />
      </a>
    );
  }

  if (variant === "banner") {
    return (
      <div
        className="rounded-[28px] overflow-hidden relative p-7 md:p-9 text-white"
        style={{
          background:
            "radial-gradient(110% 80% at 100% 0%, #ff3301 0%, #a32500 45%, #1a0a04 90%)",
        }}
      >
        <div className="grid md:grid-cols-[1.4fr_1fr] gap-6 items-center">
          <div>
            <div
              className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[11px] font-semibold tracking-wider uppercase mb-4"
              style={{
                background: "rgba(255,255,255,0.16)",
                color: "#fff",
              }}
            >
              <span
                className="w-1.5 h-1.5 rounded-full"
                style={{ background: "#fff" }}
              />
              The Newsletter
            </div>
            <h3
              className="font-display"
              style={{
                fontSize: "clamp(26px, 3vw, 36px)",
                letterSpacing: "-0.025em",
                lineHeight: 1.1,
              }}
            >
              {NEWSLETTER_NAME}
            </h3>
            <p className="mt-3 text-white/85 text-[15px] leading-relaxed max-w-md">
              {NEWSLETTER_TAGLINE}
            </p>
          </div>
          <div className="flex md:justify-end">
            <a
              href={SUBSTACK_URL}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 px-5 py-3.5 rounded-full font-medium transition hover:bg-neutral-100"
              style={{ background: "#fff", color: "#0A0A0A" }}
            >
              Subscribe on Substack
              <ArrowUpRight size={16} />
            </a>
          </div>
        </div>
      </div>
    );
  }

  // default: card
  return (
    <div
      className="rounded-3xl p-7 md:p-8 text-white relative overflow-hidden"
      style={{
        background:
          "linear-gradient(140deg, #0A0A0A 0%, #2a0a04 60%, #ff3301 145%)",
      }}
    >
      <div
        className="absolute -top-16 -right-16 w-56 h-56 rounded-full pointer-events-none"
        style={{
          background:
            "radial-gradient(50% 50% at 50% 50%, rgba(255,51,1,0.45) 0%, rgba(255,51,1,0) 100%)",
        }}
      />
      <div className="relative">
        <div
          className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[11px] font-semibold tracking-wider uppercase mb-4"
          style={{
            background: "rgba(255,51,1,0.22)",
            color: "#ffb097",
          }}
        >
          <Mail size={12} />
          Never miss a report
        </div>
        <h3
          className="font-display"
          style={{
            fontSize: "clamp(22px, 2.4vw, 30px)",
            letterSpacing: "-0.025em",
            lineHeight: 1.15,
          }}
        >
          Get {NEWSLETTER_NAME} in your inbox.
        </h3>
        <p className="mt-3 text-white/80 text-[15px] leading-relaxed max-w-md">
          {NEWSLETTER_TAGLINE}
        </p>
        <a
          href={SUBSTACK_URL}
          target="_blank"
          rel="noreferrer"
          className="mt-6 inline-flex items-center gap-2 px-5 py-3 rounded-full font-medium transition"
          style={{ background: "#fff", color: "#0A0A0A" }}
        >
          Subscribe on Substack
          <ArrowUpRight size={16} />
        </a>
      </div>
    </div>
  );
}
